import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mic, Square, Keyboard, ChevronRight, Lightbulb, Check, RotateCcw, Tag } from 'lucide-react';

const A1_EXERCISES = [
  {
    id: 'dev-1',
    typeLabel: 'Développe la phrase',
    consigne: "Fais une phrase correcte et naturelle. Ajoute le plus d'informations possible.",
    starter: 'Je nettoie la cuisine…',
    hints: ['avec qui ?', 'quand ?', 'pourquoi ?'],
    modele: 'Je nettoie la cuisine avec ma sœur après le dîner.',
    feedback: 'Très bien !',
    conseil: 'Ajoute : avec qui ? quand ? pourquoi ?',
  },
  {
    id: 'dev-2',
    typeLabel: 'Développe la phrase',
    consigne: "Fais une phrase correcte et naturelle. Ajoute le plus d'informations possible.",
    starter: 'Je parle au téléphone…',
    hints: ['une personne', 'un moment', 'un lieu'],
    modele: 'Je parle au téléphone avec mon ami le soir.',
    feedback: 'Bonne phrase !',
    conseil: 'Ajoute : une personne, un moment, un lieu.',
  },
  {
    id: 'dev-3',
    typeLabel: 'Développe la phrase',
    consigne: "Fais une phrase correcte et naturelle. Ajoute le plus d'informations possible.",
    starter: 'Je regarde les étoiles…',
    hints: ['un lieu', 'une personne', 'un moment'],
    modele: 'Je regarde les étoiles dans le jardin avec ma famille.',
    feedback: 'Belle idée !',
    conseil: 'Ajoute : un lieu, une personne, un moment.',
  },
  {
    id: 'dev-4',
    typeLabel: 'Développe la phrase',
    consigne: "Fais une phrase correcte et naturelle. Ajoute le plus d'informations possible.",
    starter: 'Je coupe les légumes…',
    hints: ['pourquoi ?', 'quand ?', 'avec qui ?'],
    modele: 'Je coupe les légumes pour préparer le dîner.',
    feedback: 'Très bien !',
    conseil: 'Ajoute : pourquoi ? quand ? avec qui ?',
  },
  {
    id: 'dev-5',
    typeLabel: 'Développe la phrase',
    consigne: "Fais une phrase correcte et naturelle. Ajoute le plus d'informations possible.",
    starter: 'Je prépare mon sac…',
    hints: ['un moment', 'une raison', 'un lieu'],
    modele: "Je prépare mon sac avant d'aller à l'école.",
    feedback: 'Bonne phrase !',
    conseil: 'Ajoute : un moment, une raison, un lieu.',
  },
  {
    id: 'dev-6',
    typeLabel: 'Développe la phrase',
    consigne: "Fais une phrase correcte et naturelle. Ajoute le plus d'informations possible.",
    starter: 'Je ris avec mes amis…',
    hints: ['quand ?', 'pourquoi ?', 'où ?'],
    modele: 'Je ris avec mes amis pendant la pause.',
    feedback: 'Belle idée !',
    conseil: 'Ajoute : quand ? pourquoi ? où ?',
  },
  {
    id: 'dev-7',
    typeLabel: 'Développe la phrase',
    consigne: "Fais une phrase correcte et naturelle. Ajoute le plus d'informations possible.",
    starter: 'Je pense à mes vacances…',
    hints: ['quand ?', 'pourquoi ?', 'avec qui ?'],
    modele: 'Je pense à mes vacances pendant le cours.',
    feedback: 'Très bien !',
    conseil: 'Ajoute : quand ? pourquoi ? avec qui ?',
  },
  {
    id: 'dev-8',
    typeLabel: 'Développe la phrase',
    consigne: "Fais une phrase correcte et naturelle. Ajoute le plus d'informations possible.",
    starter: "J'attends le taxi…",
    hints: ['un lieu', 'une personne', 'un moment'],
    modele: "J'attends le taxi devant la gare avec mon père.",
    feedback: 'Bonne phrase !',
    conseil: 'Ajoute : un lieu, une personne, un moment.',
  },
  {
    id: 'cont-1',
    typeLabel: 'Continue la phrase',
    consigne: 'Continue la phrase naturellement avec tes propres idées.',
    starter: "Après l'école, je…",
    hints: ['activité ?', 'avec qui ?', 'où ?'],
    modele: "Après l'école, je rentre à la maison et je fais mes devoirs avec ma sœur.",
    feedback: 'Belle idée !',
    conseil: "Utilise « et » ou « puis » pour enchaîner les actions.",
  },
  {
    id: 'cont-2',
    typeLabel: 'Continue la phrase',
    consigne: 'Continue la phrase naturellement avec tes propres idées.',
    starter: 'Quand il pleut, nous…',
    hints: ['activité ?', 'où ?', 'émotion ?'],
    modele: 'Quand il pleut, nous restons à la maison et nous regardons un film ensemble.',
    feedback: 'Très bien !',
    conseil: "Après « quand », décris une action habituelle.",
  },
  {
    id: 'cont-3',
    typeLabel: 'Continue la phrase',
    consigne: 'Termine la phrase avec une raison ou une explication.',
    starter: "J'aime écouter de la musique parce que…",
    hints: ['émotion ?', 'moment ?', 'type de musique ?'],
    modele: "J'aime écouter de la musique parce que ça me rend heureux et ça m'aide à me concentrer.",
    feedback: 'Bonne phrase !',
    conseil: "Après « parce que », donne toujours une raison claire.",
  },
  {
    id: 'ref-1',
    typeLabel: 'Reformule autrement',
    consigne: 'Exprime la même idée avec des mots différents.',
    starter: 'Il fait beau.',
    hints: ['synonyme ?', 'description ?', 'sensation ?'],
    modele: "Le temps est magnifique aujourd'hui, il y a du soleil.",
    feedback: 'Bonne reformulation !',
    conseil: "Essaie d'utiliser un adjectif différent ou d'ajouter un détail.",
  },
  {
    id: 'ref-2',
    typeLabel: 'Reformule autrement',
    consigne: 'Exprime la même idée avec des mots différents.',
    starter: 'Je suis fatigué(e).',
    hints: ['cause ?', 'intensité ?', 'conséquence ?'],
    modele: "Je n'ai plus d'énergie, j'ai besoin de me reposer.",
    feedback: 'Belle idée !',
    conseil: "Utilise une conséquence ou une cause pour enrichir ta reformulation.",
  },
  {
    id: 'ref-3',
    typeLabel: 'Reformule autrement',
    consigne: 'Exprime la même idée avec des mots différents.',
    starter: "Je n'ai pas de temps.",
    hints: ['raison ?', 'alternative ?', 'émotion ?'],
    modele: "Mon emploi du temps est très chargé en ce moment.",
    feedback: 'Très bien !',
    conseil: "Pense à utiliser une expression différente au lieu de la négation directe.",
  },
];

const A2_EXERCISES = [
  {
    id: 'a2-dev-1',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "Je vais préparer un gâteau pour l'anniversaire de ma sœur.",
    grammarTag: 'futur proche',
    hints: ['quand ?', 'avec qui ?', 'quel détail ?'],
    modele: "Je vais préparer un gâteau au chocolat pour l'anniversaire de ma sœur ce soir avec ma mère.",
    feedback: 'Très bien ! Belle utilisation du futur proche.',
    conseil: 'Ajoute : un moment, une personne, un détail.',
  },
  {
    id: 'a2-dev-2',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "Je travaille à la maison aujourd'hui parce que je suis fatigué.",
    grammarTag: 'présent',
    hints: ['une raison', 'une émotion', 'un complément'],
    modele: "Je travaille à la maison aujourd'hui parce que je suis fatigué après une longue semaine.",
    feedback: 'Bonne structure ! Présent bien employé.',
    conseil: 'Ajoute : une raison, une émotion, un complément.',
  },
  {
    id: 'a2-dev-3',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "Je nageais dans la piscine quand mes amis sont arrivés.",
    grammarTag: 'imparfait + passé composé',
    hints: ['une description', 'une action secondaire', 'une émotion'],
    modele: "Je nageais tranquillement dans la piscine quand mes amis sont arrivés avec des boissons.",
    feedback: 'Belle utilisation de l\'imparfait et du passé composé !',
    conseil: 'Ajoute : une description, une action secondaire, une émotion.',
  },
  {
    id: 'a2-dev-4',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "J'ai écouté de la musique avant de dormir.",
    grammarTag: 'passé composé',
    hints: ['quel type ?', 'quand ?', 'pourquoi ?'],
    modele: "J'ai écouté de la musique calme avant de dormir après le dîner.",
    feedback: 'Belle utilisation du passé composé !',
    conseil: 'Ajoute : quel type ? quand ? pourquoi ?',
  },
  {
    id: 'a2-dev-5',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "Je vais chercher mon téléphone car je ne le trouve pas.",
    grammarTag: 'futur proche',
    hints: ['un lieu', 'un moment', 'une émotion'],
    modele: "Je vais chercher mon téléphone dans ma chambre car je ne le trouve pas depuis ce matin.",
    feedback: 'Très bien ! Le futur proche est bien construit.',
    conseil: 'Ajoute : un lieu, un moment, une émotion.',
  },
  {
    id: 'a2-dev-6',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "Je regardais les étoiles quand j'ai entendu un bruit.",
    grammarTag: 'imparfait + passé composé',
    hints: ['un lieu', 'une description', 'une réaction'],
    modele: "Je regardais les étoiles dans le jardin quand j'ai entendu un bruit étrange derrière moi.",
    feedback: 'Belle alternance des temps !',
    conseil: 'Ajoute : un lieu, une description, une réaction.',
  },
  {
    id: 'a2-dev-7',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "Je prends un café maintenant parce que j'ai froid.",
    grammarTag: 'présent',
    hints: ['une sensation', 'un moment', 'un adjectif'],
    modele: "Je prends un café chaud maintenant parce que j'ai très froid ce matin.",
    feedback: 'Bonne structure au présent !',
    conseil: 'Ajoute : une sensation, un moment, un adjectif.',
  },
  {
    id: 'a2-dev-8',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "Je vais visiter un musée cet après-midi.",
    grammarTag: 'futur proche',
    hints: ['avec qui ?', 'pourquoi ?', 'quand ?'],
    modele: "Je vais visiter un musée cet après-midi avec mes cousins pendant les vacances.",
    feedback: 'Très bien ! Belle phrase au futur proche.',
    conseil: 'Ajoute : avec qui ? pourquoi ? quand ?',
  },
  {
    id: 'a2-dev-9',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "J'ai fait mes devoirs après être rentré chez moi.",
    grammarTag: 'passé composé',
    hints: ['une deuxième action', 'un détail', 'un moment'],
    modele: "J'ai fait mes devoirs après être rentré chez moi et avoir mangé un sandwich.",
    feedback: 'Belle utilisation du passé composé enchaîné !',
    conseil: 'Ajoute : une deuxième action, un détail, un moment.',
  },
  {
    id: 'a2-dev-10',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "Je marchais dans la rue quand j'ai vu mon professeur.",
    grammarTag: 'imparfait + passé composé',
    hints: ['un lieu', 'une description', 'une réaction'],
    modele: "Je marchais dans la rue près de l'école quand j'ai vu mon professeur devant un café.",
    feedback: 'Bonne combinaison de l\'imparfait et du passé composé !',
    conseil: 'Ajoute : un lieu, une description, une réaction.',
  },
  {
    id: 'a2-dev-11',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "Je cherche une nouvelle activité à faire le soir parce que je veux apprendre quelque chose de différent.",
    grammarTag: 'présent',
    hints: ['une motivation', 'une émotion', 'une activité précise'],
    modele: "Je cherche une nouvelle activité à faire le soir parce que je veux apprendre quelque chose de différent et rencontrer de nouvelles personnes.",
    feedback: 'Belle structure ! Présent bien employé.',
    conseil: 'Ajoute : une motivation, une émotion, une activité précise.',
  },
  {
    id: 'a2-dev-12',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "Je planifie mon emploi du temps pour la semaine afin de mieux organiser mes études et mon travail.",
    grammarTag: 'présent',
    hints: ['un moment', 'une raison', 'un objectif'],
    modele: "Je planifie mon emploi du temps pour la semaine afin de mieux organiser mes études et mon travail avant les examens.",
    feedback: 'Très bien ! Phrase claire et bien construite.',
    conseil: 'Ajoute : un moment, une raison, un objectif.',
  },
  {
    id: 'a2-dev-13',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "Je joue dans le parc avec mes amis après l'école.",
    grammarTag: 'présent',
    hints: ['une condition', 'un moment', 'une activité secondaire'],
    modele: "Je joue dans le parc avec mes amis après l'école quand il fait beau.",
    feedback: 'Belle phrase au présent !',
    conseil: 'Ajoute : une condition, un moment, une activité secondaire.',
  },
  {
    id: 'a2-dev-14',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels et utilise correctement le temps demandé.",
    starter: "Je cherche mon téléphone car je ne sais plus où je l'ai mis.",
    grammarTag: 'présent + passé composé',
    hints: ['un lieu', 'un moment', 'une action précédente'],
    modele: "Je cherche mon téléphone car je ne sais plus où je l'ai mis après être rentré à la maison.",
    feedback: 'Belle combinaison des temps !',
    conseil: 'Ajoute : un lieu, un moment, une action précédente.',
  },
];

const B1_EXERCISES = [
  {
    id: 'b1-dev-1',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "Je travaille sur ce projet depuis une semaine pour que tout soit prêt avant vendredi.",
    grammarTag: 'présent + subordonnée de but',
    hints: ['un objectif', 'une conséquence', 'une émotion'],
    modele: "Je travaille sur ce projet depuis une semaine pour que tout soit prêt avant vendredi et pour éviter les problèmes de dernière minute.",
    feedback: "Belle subordonnée de but ! La structure est naturelle.",
    conseil: 'Ajoute : un objectif, une conséquence, une émotion.',
  },
  {
    id: 'b1-dev-2',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "J'ai rencontré un ami hier avec qui je voulais parler depuis longtemps.",
    grammarTag: 'passé composé + imparfait',
    hints: ['une raison', 'un souvenir', 'une émotion'],
    modele: "J'ai rencontré un ami hier avec qui je voulais parler depuis longtemps parce que nous avions eu un malentendu.",
    feedback: "Belle alternance passé composé / imparfait !",
    conseil: 'Ajoute : une raison, un souvenir, une émotion.',
  },
  {
    id: 'b1-dev-3',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "Je finirai ce rapport demain, même si je sais que ce ne sera pas parfait.",
    grammarTag: 'futur simple + concession',
    hints: ['une obligation', 'une justification', 'une opinion'],
    modele: "Je finirai ce rapport demain, même si je sais que ce ne sera pas parfait, car je dois absolument respecter la date limite.",
    feedback: "Très bien ! Belle concession + justification.",
    conseil: 'Ajoute : une obligation, une justification, une opinion.',
  },
  {
    id: 'b1-dev-4',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "Je voulais t'appeler plus tôt, mais mon téléphone ne fonctionnait plus.",
    grammarTag: 'imparfait + passé composé',
    hints: ['une cause', 'une conséquence', 'un détail'],
    modele: "Je voulais t'appeler plus tôt, mais mon téléphone ne fonctionnait plus après être tombé dans l'eau.",
    feedback: "Belle structure imparfait / passé composé !",
    conseil: 'Ajoute : une cause, une conséquence, un détail.',
  },
  {
    id: 'b1-dev-5',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "Je partirai en voyage dès que j'aurai économisé assez d'argent.",
    grammarTag: 'futur simple + futur antérieur',
    hints: ['une destination', 'un objectif', 'une durée'],
    modele: "Je partirai en voyage dès que j'aurai économisé assez d'argent pour visiter plusieurs pays européens pendant l'été.",
    feedback: "Excellent ! Le futur antérieur est bien employé.",
    conseil: 'Ajoute : une destination, un objectif, une durée.',
  },
  {
    id: 'b1-dev-6',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "J'espère que tu pourras venir ce soir, même si tu es très occupé.",
    grammarTag: 'présent + concession',
    hints: ['une émotion', 'une raison', 'une précision'],
    modele: "J'espère que tu pourras venir ce soir, même si tu es très occupé, parce que cette soirée est importante pour moi.",
    feedback: "Belle concession + nuance émotionnelle !",
    conseil: 'Ajoute : une émotion, une raison, une précision.',
  },
  {
    id: 'b1-dev-7',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "J'aurais aimé que tu me préviennes avant de changer les plans.",
    grammarTag: 'conditionnel passé + subjonctif',
    hints: ['une conséquence', 'une émotion', 'une justification'],
    modele: "J'aurais aimé que tu me préviennes avant de changer les plans afin que je puisse mieux m'organiser.",
    feedback: "Bravo ! Conditionnel passé + subjonctif très bien combinés.",
    conseil: 'Ajoute : une conséquence, une émotion, une justification.',
  },
  {
    id: 'b1-dev-8',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "Je te téléphonerai dès que j'aurai compris la situation.",
    grammarTag: 'futur simple + futur antérieur',
    hints: ['une intention', 'une précision', 'une émotion'],
    modele: "Je te téléphonerai dès que j'aurai compris la situation pour t'expliquer ce qui s'est réellement passé.",
    feedback: "Très bien ! Belle anticipation grammaticale.",
    conseil: 'Ajoute : une intention, une précision, une émotion.',
  },
  {
    id: 'b1-dev-9',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "Je voudrais que tu m'expliques ce qui s'est vraiment passé.",
    grammarTag: 'conditionnel présent + subjonctif',
    hints: ['une émotion', 'une conséquence', 'une intention'],
    modele: "Je voudrais que tu m'expliques ce qui s'est vraiment passé afin d'éviter les malentendus entre nous.",
    feedback: "Belle utilisation du subjonctif !",
    conseil: 'Ajoute : une émotion, une conséquence, une intention.',
  },
  {
    id: 'b1-dev-10',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "Je suis allé au musée parce que j'avais entendu dire qu'il y avait une nouvelle exposition.",
    grammarTag: 'passé composé + plus-que-parfait',
    hints: ['un thème', 'une opinion', 'une description'],
    modele: "Je suis allé au musée parce que j'avais entendu dire qu'il y avait une nouvelle exposition sur l'art moderne.",
    feedback: "Excellent enchaînement de temps passés !",
    conseil: 'Ajoute : un thème, une opinion, une description.',
  },
  {
    id: 'b1-dev-11',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "J'ai décidé de changer de travail parce que j'avais besoin de nouveaux défis.",
    grammarTag: 'passé composé + imparfait',
    hints: ['une motivation', 'une émotion', 'une conséquence'],
    modele: "J'ai décidé de changer de travail parce que j'avais besoin de nouveaux défis et d'un environnement plus motivant.",
    feedback: "Très bien ! La structure narrative est claire.",
    conseil: 'Ajoute : une motivation, une émotion, une conséquence.',
  },
  {
    id: 'b1-dev-12',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "Je veux que tu me dises la vérité, même si elle est difficile à entendre.",
    grammarTag: 'présent + subjonctif + concession',
    hints: ['une intention', 'une émotion', 'une conséquence'],
    modele: "Je veux que tu me dises la vérité, même si elle est difficile à entendre, afin que nous puissions résoudre ce problème ensemble.",
    feedback: "Belle utilisation du subjonctif et de la concession !",
    conseil: 'Ajoute : une intention, une émotion, une conséquence.',
  },
  {
    id: 'b1-dev-13',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "Je serais parti plus tôt si j'avais su qu'il y aurait autant de trafic.",
    grammarTag: 'conditionnel passé + plus-que-parfait',
    hints: ['un lieu', 'un moment', 'une conséquence'],
    modele: "Je serais parti plus tôt si j'avais su qu'il y aurait autant de trafic sur l'autoroute ce matin.",
    feedback: "Excellent ! Hypothèse irréelle bien construite.",
    conseil: 'Ajoute : un lieu, un moment, une conséquence.',
  },
  {
    id: 'b1-dev-14',
    typeLabel: 'Développe la phrase',
    consigne: "Développe la phrase avec des détails naturels, des connecteurs et utilise correctement la structure demandée.",
    starter: "Je te rappelle dès que j'aurai trouvé une solution.",
    grammarTag: 'futur simple + futur antérieur',
    hints: ['un objectif', 'une précision', 'une émotion'],
    modele: "Je te rappelle dès que j'aurai trouvé une solution pour régler ce problème définitivement.",
    feedback: "Très bien ! Le futur antérieur fonctionne parfaitement ici.",
    conseil: 'Ajoute : un objectif, une précision, une émotion.',
  },
];

// B2 uses sophisticated labels: "Proposition possible" + "Pour aller plus loin" (bullet list).
const B2_CONSIGNE = 'Développez cette phrase de manière naturelle et nuancée en respectant le registre, le temps verbal et la logique du discours.';
const B2_EXERCISES = [
  {
    id: 'b2-dev-1',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "Quand j'aurai terminé mes études, je chercherai un emploi qui me permettra de voyager régulièrement.",
    grammarTag: 'futur antérieur',
    hints: ['quelles conséquences ?', 'dans quel contexte ?', 'avec quelle émotion ?'],
    modele: "Quand j'aurai terminé mes études, je chercherai un emploi qui me permettra de voyager régulièrement afin d'acquérir une expérience internationale et de découvrir de nouvelles cultures.",
    feedback: 'Structure fluide et naturelle. Belle anticipation temporelle.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['développez votre motivation', 'ajoutez une conséquence', 'comparez avec votre situation actuelle'],
  },
  {
    id: 'b2-dev-2',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "Je doute qu'il ait compris la véritable intention du projet, même si je lui ai expliqué plusieurs fois.",
    grammarTag: 'subjonctif + concession',
    hints: ['quelle nuance ajouter ?', 'pourquoi ?', 'quelle précision ?'],
    modele: "Je doute qu'il ait compris la véritable intention du projet, même si je lui ai expliqué plusieurs fois, car certains aspects restent particulièrement complexes.",
    feedback: 'Bonne gestion de la concession. Le subjonctif est précis.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['ajoutez une hypothèse', 'développez la difficulté', 'nuancez votre jugement'],
  },
  {
    id: 'b2-dev-3',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "Dès que tu auras fini la présentation, nous pourrons discuter des prochaines étapes.",
    grammarTag: 'futur antérieur',
    hints: ['quel objectif ?', 'quels enjeux ?', 'quelle contrainte ?'],
    modele: "Dès que tu auras fini la présentation, nous pourrons discuter des prochaines étapes afin de mieux répartir les responsabilités au sein de l'équipe.",
    feedback: 'Votre argumentation est claire. Belle séquence temporelle.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['ajoutez un objectif', 'développez les enjeux', 'introduisez une contrainte'],
  },
  {
    id: 'b2-dev-4',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "J'avais déjà envoyé le rapport quand on m'a demandé de le modifier encore une fois.",
    grammarTag: 'plus-que-parfait',
    hints: ['quelles conséquences ?', 'avec quelle émotion ?', 'quelle réaction ?'],
    modele: "J'avais déjà envoyé le rapport quand on m'a demandé de le modifier encore une fois, ce qui m'a obligé à revoir entièrement certaines parties du document.",
    feedback: 'Cohérence temporelle parfaite. Bonne explicitation de la conséquence.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['ajoutez une réaction personnelle', 'développez les conséquences', 'exprimez une frustration'],
  },
  {
    id: 'b2-dev-5',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "Je suis heureuse que tu aies trouvé une solution avant que la situation ne devienne plus compliquée.",
    grammarTag: 'subjonctif',
    hints: ['quelle émotion ?', 'quelle hypothèse ?', 'quelles conséquences ?'],
    modele: "Je suis heureuse que tu aies trouvé une solution avant que la situation ne devienne plus compliquée, car cela aurait pu créer davantage de tensions dans l'équipe.",
    feedback: 'Belle utilisation du subjonctif. Émotion et nuance bien équilibrées.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['développez les conséquences possibles', 'ajoutez une émotion', 'introduisez une hypothèse'],
  },
  {
    id: 'b2-dev-6',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "Nous aurons terminé ce travail avant que le client ne revienne de son voyage.",
    grammarTag: 'futur antérieur + subjonctif',
    hints: ['quelle justification ?', 'quels enjeux ?', 'quelle nuance ?'],
    modele: "Nous aurons terminé ce travail avant que le client ne revienne de son voyage afin de pouvoir lui présenter une version finale parfaitement organisée.",
    feedback: 'Excellente articulation temporelle. Discours professionnel bien tenu.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['ajoutez une justification', 'développez les enjeux professionnels', 'nuancez la difficulté'],
  },
  {
    id: 'b2-dev-7',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "Si j'avais su que le projet avancerait si vite, je me serais préparé plus tôt.",
    grammarTag: 'conditionnel passé',
    hints: ['quelles conséquences ?', 'quelle leçon ?', 'quelle autocritique ?'],
    modele: "Si j'avais su que le projet avancerait si vite, je me serais préparé plus tôt afin d'éviter certaines erreurs liées au manque d'organisation.",
    feedback: 'Hypothèse irréelle bien construite. Belle prise de recul.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['développez les conséquences', 'ajoutez une autocritique', 'introduisez une leçon tirée de l\'expérience'],
  },
  {
    id: 'b2-dev-8',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "Je continuerai cette formation afin que mes compétences soient reconnues officiellement.",
    grammarTag: 'subjonctif',
    hints: ['quel objectif ?', 'quelle motivation ?', 'quelle comparaison ?'],
    modele: "Je continuerai cette formation afin que mes compétences soient reconnues officiellement et que je puisse évoluer plus facilement dans ma carrière.",
    feedback: 'Belle utilisation du subjonctif. L\'intention est claire et structurée.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['développez vos objectifs', 'ajoutez une motivation personnelle', 'comparez avec votre situation actuelle'],
  },
  {
    id: 'b2-dev-9',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "Lorsque j'aurai tout organisé, je t'enverrai les documents nécessaires.",
    grammarTag: 'futur antérieur',
    hints: ['quelle précision ?', 'quel contexte ?', 'quelle contrainte ?'],
    modele: "Lorsque j'aurai tout organisé, je t'enverrai les documents nécessaires afin que tu puisses préparer la réunion dans de bonnes conditions.",
    feedback: 'Structure fluide et naturelle. Anticipation temporelle maîtrisée.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['ajoutez une précision', 'développez le contexte', 'introduisez une contrainte'],
  },
  {
    id: 'b2-dev-10',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "Je pensais que tu serais plus prudent, mais tu as pris une décision très risquée.",
    grammarTag: 'opposition + nuance',
    hints: ['quelle nuance ?', 'quelles conséquences ?', 'quel point de vue ?'],
    modele: "Je pensais que tu serais plus prudent, mais tu as pris une décision très risquée sans prendre en compte les conséquences possibles.",
    feedback: 'Bonne gestion de l\'opposition. Vous pouvez développer davantage la nuance.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['nuancez votre critique', 'ajoutez une conséquence', 'développez votre point de vue'],
  },
  {
    id: 'b2-dev-11',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "Il est possible que tu aies raison, mais j'aimerais vérifier les informations avant de conclure.",
    grammarTag: 'subjonctif + nuance',
    hints: ['quelle hypothèse ?', 'quel doute ?', 'quelle nuance ?'],
    modele: "Il est possible que tu aies raison, mais j'aimerais vérifier les informations avant de conclure afin d'éviter toute interprétation erronée.",
    feedback: 'Belle nuance argumentative. Le doute est exprimé avec finesse.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['ajoutez une hypothèse', 'développez le doute', 'nuancez votre opinion'],
  },
  {
    id: 'b2-dev-12',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "Quand ils auront signé le contrat, nous pourrons commencer la première phase du projet.",
    grammarTag: 'futur antérieur',
    hints: ['quels enjeux ?', 'quelle contrainte ?', 'quel objectif ?'],
    modele: "Quand ils auront signé le contrat, nous pourrons commencer la première phase du projet tout en respectant le calendrier fixé par l'entreprise.",
    feedback: 'Structure claire et professionnelle. Bonne anticipation des étapes.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['développez les enjeux', 'ajoutez une contrainte', 'précisez les objectifs'],
  },
  {
    id: 'b2-dev-13',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "Je regrettais que tu n'aies pas participé à la réunion, car ton avis aurait été très utile.",
    grammarTag: 'subjonctif + imparfait',
    hints: ['quelles conséquences ?', 'quelle émotion ?', 'quelle hypothèse ?'],
    modele: "Je regrettais que tu n'aies pas participé à la réunion, car ton avis aurait été très utile pour résoudre certains désaccords au sein de l'équipe.",
    feedback: 'Belle combinaison subjonctif + imparfait. Émotion et regret bien rendus.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['développez les conséquences', 'ajoutez une émotion', 'introduisez une hypothèse'],
  },
  {
    id: 'b2-dev-14',
    typeLabel: 'Développe la phrase',
    consigne: B2_CONSIGNE,
    starter: "Si tu m'avais prévenu plus tôt, je t'aurais accompagné sans hésiter.",
    grammarTag: 'plus-que-parfait + conditionnel passé',
    hints: ['quelle justification ?', 'quel contexte ?', 'quelle nuance ?'],
    modele: "Si tu m'avais prévenu plus tôt, je t'aurais accompagné sans hésiter afin de t'aider à gérer cette situation difficile.",
    feedback: 'Hypothèse irréelle au passé parfaitement construite. Belle empathie exprimée.',
    modeleLabel: 'Proposition possible',
    conseilLabel: 'Pour aller plus loin',
    conseilItems: ['ajoutez une justification', 'développez le contexte', 'nuancez votre réaction'],
  },
];

const LEVELS_DATA = { A1: A1_EXERCISES, A2: A2_EXERCISES, B1: B1_EXERCISES, B2: B2_EXERCISES };
const LEVELS = ['A1', 'A2', 'B1', 'B2'];

function RecordButton({ onRecorded, testId }) {
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        if (onRecorded) onRecorded(url);
        stream.getTracks().forEach(t => t.stop());
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
    } catch { alert('Microphone non disponible.'); }
  };

  const stop = () => {
    if (mrRef.current && recording) {
      mrRef.current.stop();
      setRecording(false);
    }
  };

  return (
    <div className="space-y-2">
      {!recording ? (
        <button onClick={start} className="w-full flex items-center justify-center gap-2 bg-[#1A2F4A] text-white rounded-xl py-3.5 text-sm font-medium hover:bg-[#243a58] transition-colors" data-testid={testId}>
          <Mic className="w-4 h-4" /> Enregistrer ma réponse
        </button>
      ) : (
        <button onClick={stop} className="w-full flex items-center justify-center gap-2 bg-red-500 text-white rounded-xl py-3.5 text-sm font-medium animate-pulse" data-testid={`${testId}-stop`}>
          <Square className="w-4 h-4" /> Arrêter l'enregistrement
        </button>
      )}
      {audioUrl && <audio controls src={audioUrl} className="w-full h-10" data-testid={`${testId}-playback`} />}
    </div>
  );
}

function ExerciseCard({ exercise, index, total, onNext, isLast }) {
  const [mode, setMode] = useState(null);
  const [text, setText] = useState('');
  const [hasRecording, setHasRecording] = useState(false);
  const [validated, setValidated] = useState(false);
  const [showModele, setShowModele] = useState(false);

  const canValidate = mode === 'write' ? text.trim().length > 0 : hasRecording;
  const colorMap = { 'Développe la phrase': '#3B82F6', 'Continue la phrase': '#10B981', 'Reformule autrement': '#F59E0B' };
  const color = colorMap[exercise.typeLabel] || '#3B82F6';

  const handleValidate = () => {
    setValidated(true);
    setTimeout(() => setShowModele(true), 1200);
  };

  const handleRetry = () => {
    setMode(null);
    setText('');
    setHasRecording(false);
    setValidated(false);
    setShowModele(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E8E4DC] overflow-hidden shadow-sm" data-testid={`exercise-${exercise.id}`}>
      {/* Progress */}
      <div className="px-6 pt-5 pb-0">
        <div className="flex items-center justify-end mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>{exercise.typeLabel}</span>
        </div>
        <div className="w-full h-1 bg-[#F3F4F6] rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${((index + 1) / total) * 100}%`, background: color }} />
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-5 space-y-5">
        {/* Consigne */}
        <p className="text-sm text-[#6B4E37] leading-relaxed" data-testid={`consigne-${exercise.id}`}>
          {exercise.consigne}
        </p>

        {/* Starter */}
        <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl px-5 py-4" data-testid={`starter-${exercise.id}`}>
          <p className="text-lg font-bold text-[#1A2F4A] leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
            {exercise.grammarTag && <span className="text-[#D4AF37] mr-2" aria-hidden="true">➡️</span>}
            {exercise.starter}
          </p>
        </div>

        {/* Grammar tag (A2+) */}
        {exercise.grammarTag && (
          <div className="flex items-center gap-2" data-testid={`grammar-tag-${exercise.id}`}>
            <div className="inline-flex items-center gap-1.5 bg-[#1A2F4A]/5 border border-[#1A2F4A]/10 text-[#1A2F4A] rounded-full px-3 py-1.5">
              <Tag className="w-3 h-3 text-[#D4AF37]" />
              <span className="text-[11px] font-bold italic">{exercise.grammarTag}</span>
            </div>
          </div>
        )}

        {/* Hints */}
        <div className="flex flex-wrap gap-1.5" data-testid={`hints-${exercise.id}`}>
          {exercise.hints.map((h, i) => (
            <span key={i} className="text-[10px] bg-[#F3F4F6] text-[#999] rounded-full px-2.5 py-1 font-medium">{h}</span>
          ))}
        </div>

        {/* Mode selection */}
        {!validated && !mode && (
          <div className="flex gap-2" data-testid={`mode-select-${exercise.id}`}>
            <button
              onClick={() => setMode('voice')}
              className="flex-1 flex items-center justify-center gap-2 bg-[#1A2F4A] text-white rounded-xl py-3 text-xs font-bold hover:bg-[#243a58] transition-colors"
              data-testid={`mode-voice-${exercise.id}`}>
              <Mic className="w-3.5 h-3.5" /> Parler
            </button>
            <button
              onClick={() => setMode('write')}
              className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-[#1A2F4A] text-[#1A2F4A] rounded-xl py-3 text-xs font-bold hover:bg-[#FAF7F0] transition-colors"
              data-testid={`mode-write-${exercise.id}`}>
              <Keyboard className="w-3.5 h-3.5" /> Écrire
            </button>
          </div>
        )}

        {/* Voice mode */}
        {!validated && mode === 'voice' && (
          <div className="space-y-3">
            <RecordButton onRecorded={() => setHasRecording(true)} testId={`record-${exercise.id}`} />
            {hasRecording && (
              <button
                onClick={handleValidate}
                className="w-full py-3 rounded-xl text-sm font-bold transition-colors"
                style={{ background: color, color: '#fff' }}
                data-testid={`validate-${exercise.id}`}>
                Valider
              </button>
            )}
          </div>
        )}

        {/* Write mode */}
        {!validated && mode === 'write' && (
          <div className="space-y-3">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Écris ta phrase ici…"
              className="w-full border border-[#E8E4DC] rounded-xl px-4 py-3 text-sm text-[#1A2F4A] placeholder-[#ccc] focus:outline-none focus:border-[#D4AF37] transition-colors resize-none leading-relaxed"
              rows={3}
              data-testid={`textarea-${exercise.id}`}
            />
            {canValidate && (
              <button
                onClick={handleValidate}
                className="w-full py-3 rounded-xl text-sm font-bold transition-colors"
                style={{ background: color, color: '#fff' }}
                data-testid={`validate-${exercise.id}`}>
                Valider
              </button>
            )}
          </div>
        )}

        {/* Feedback */}
        {validated && (
          <div className="space-y-4">
            {/* Encouraging feedback */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3.5 flex items-center gap-3" data-testid={`feedback-${exercise.id}`}>
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-sm font-bold text-emerald-700">{exercise.feedback}</p>
            </div>

            {/* Model answer — revealed after delay */}
            {showModele && (
              <div className="space-y-3" data-testid={`modele-${exercise.id}`}>
                <div className="bg-[#1A2F4A] rounded-xl px-5 py-4">
                  <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold mb-2">
                    {exercise.modeleLabel || 'Bonne réponse'}
                  </p>
                  <p className="text-sm text-white/90 leading-relaxed italic">"{exercise.modele}"</p>
                </div>

                <div className="flex items-start gap-2.5 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl px-4 py-3" data-testid={`conseil-${exercise.id}`}>
                  <Lightbulb className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold mb-1.5">
                      {exercise.conseilLabel || 'Petit conseil'}
                    </p>
                    {Array.isArray(exercise.conseilItems) ? (
                      <ul className="space-y-1">
                        {exercise.conseilItems.map((item, i) => (
                          <li key={i} className="text-xs text-[#6B4E37] leading-relaxed flex items-start gap-2">
                            <span className="text-[#D4AF37] mt-0.5">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-[#6B4E37] leading-relaxed">{exercise.conseil}</p>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2" data-testid={`actions-${exercise.id}`}>
                  <button
                    onClick={handleRetry}
                    className="flex-1 flex items-center justify-center gap-2 bg-white border border-[#E8E4DC] text-[#1A2F4A] rounded-xl py-2.5 text-xs font-bold hover:border-[#D4AF37] transition-colors"
                    data-testid={`retry-${exercise.id}`}>
                    <RotateCcw className="w-3.5 h-3.5" /> Réessayer
                  </button>
                  {!isLast && (
                    <button
                      onClick={onNext}
                      className="flex-1 flex items-center justify-center gap-2 bg-[#1A2F4A] text-white rounded-xl py-2.5 text-xs font-bold hover:bg-[#243a58] transition-colors"
                      data-testid={`next-${exercise.id}`}>
                      Exercice suivant <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ReconstruisLaPhrase() {
  const cardRefs = useRef({});
  const [level, setLevel] = useState('A1');
  const [activeIndex, setActiveIndex] = useState(0);

  const exercises = LEVELS_DATA[level];
  const total = exercises.length;

  // Reset active card when level changes
  useEffect(() => {
    setActiveIndex(0);
  }, [level]);

  // Observe which card is currently in view to update the top progress bar
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const idx = exercises.findIndex((ex) => ex.id === visible.target.dataset.exerciseId);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { threshold: [0.4, 0.6] }
    );
    exercises.forEach((ex) => {
      const el = cardRefs.current[ex.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [level, exercises]);

  const scrollToNext = (currentIndex) => {
    const nextId = exercises[currentIndex + 1]?.id;
    if (nextId && cardRefs.current[nextId]) {
      cardRefs.current[nextId].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      {/* Sticky top progress bar */}
      <div className="sticky top-0 z-30 bg-[#FAF7F0]/95 backdrop-blur-sm border-b border-[#E8E4DC]" data-testid="top-progress-bar">
        <div className="max-w-[520px] mx-auto px-6 py-2.5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-[#999] font-medium tracking-wide" data-testid="top-progress-label">
              Exercice {activeIndex + 1} sur {total}
            </span>
            <span className="text-[10px] text-[#1A2F4A] font-bold uppercase tracking-wider">{level}</span>
          </div>
          <div className="w-full h-[3px] bg-[#F3F4F6] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${((activeIndex + 1) / total) * 100}%`, background: '#D4AF37' }}
            />
          </div>
        </div>
      </div>

      {/* Header */}
      <section className="pt-10 pb-6">
        <div className="max-w-[520px] mx-auto px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-[10px] text-[#999] mb-6 flex-wrap" data-testid="breadcrumb">
            <Link to="/speaking-lab" className="hover:text-[#D4AF37] transition-colors">Speaking Lab</Link>
            <ChevronRight className="w-3 h-3" />
            <span>Atelier d'Expression</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#1A2F4A] font-bold">Reconstruis la phrase</span>
          </div>

          <div className="text-center mb-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A2F4A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="page-title">
              Reconstruis la phrase
            </h1>
            <p className="text-xs text-[#6B4E37] italic max-w-sm mx-auto">
              {level === 'A1'
                ? 'Construis des phrases naturelles, mot par mot, idée par idée.'
                : 'Développe tes réflexes de syntaxe, de temps et de connecteurs en parlant naturellement.'}
            </p>
          </div>

          {/* Level tabs */}
          <div className="flex justify-center gap-2" data-testid="level-tabs">
            {LEVELS.map((lv) => (
              <button
                key={lv}
                onClick={() => setLevel(lv)}
                className={`text-[11px] font-bold tracking-wider rounded-full px-4 py-1.5 transition-all ${
                  level === lv
                    ? 'bg-[#1A2F4A] text-white shadow-sm'
                    : 'bg-white text-[#1A2F4A] border border-[#E8E4DC] hover:border-[#D4AF37]'
                }`}
                data-testid={`level-tab-${lv}`}
              >
                {lv}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Exercises */}
      <section className="pb-20">
        <div className="max-w-[520px] mx-auto px-6 space-y-5" data-testid="exercises-list">
          {exercises.map((ex, i) => (
            <div
              key={ex.id}
              data-exercise-id={ex.id}
              ref={el => { cardRefs.current[ex.id] = el; }}
            >
              <ExerciseCard
                exercise={ex}
                index={i}
                total={total}
                onNext={() => scrollToNext(i)}
                isLast={i === total - 1}
              />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
